import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IconNotificationsOutline } from "@boligportal/bricks-icons";
import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { ensureAgents } from "~/api/agents/queries";
import { AgentNotFoundComponent } from "~/routes/(restricted)/byggeradar/-components/AgentNotFoundComponent";
import { ProposalList } from "~/routes/(restricted)/byggeradar/-components/ProposalList";
import { ProposalPagination } from "~/routes/(restricted)/byggeradar/-components/ProposalPagination";
import { BaseMapLayout } from "~/routes/(restricted)/byggeradar/-components/layout/BaseMapLayout";
import { SearchResultMap } from "~/routes/(restricted)/byggeradar/-components/map/SearchResultMap";
import { AgentTopBar } from "~/routes/(restricted)/byggeradar/-components/navigation/AgentTopBar";
import { MarkAllSeenButton } from "~/routes/(restricted)/byggeradar/-components/navigation/MarkAllSeenButton";
import { useAgents } from "~/routes/(restricted)/byggeradar/-hooks/useAgents";
import { useByggeRadarTracking } from "~/routes/(restricted)/byggeradar/-hooks/useByggeRadarTracking";
import { useMapViewport } from "~/routes/(restricted)/byggeradar/-hooks/useMapViewport";
import { useProposalsLoader } from "~/routes/(restricted)/byggeradar/-hooks/useProposalsLoader";
import { useProposalsMapMarkers } from "~/routes/(restricted)/byggeradar/-hooks/useProposalsMapMarkers";
import { useSelectionData } from "~/routes/(restricted)/byggeradar/-hooks/useSelectionData";
import { useTrackOncePer } from "~/routes/(restricted)/byggeradar/-hooks/useTrackOncePer";
import { useUpdateSearch } from "~/routes/(restricted)/byggeradar/-hooks/useUpdateSearch";
import { proposalSearchSchema } from "~/routes/(restricted)/byggeradar/-schemas/proposal-search";
import { PAGINATION_PAGE_SIZE } from "~/routes/(restricted)/byggeradar/-utils/constants";
import { type ProposalSource } from "~/routes/(restricted)/byggeradar/-utils/proposal-source";
import { isDifferent } from "~/routes/(restricted)/byggeradar/-utils/search-terms";
import { agentTrackingProperties } from "~/routes/(restricted)/byggeradar/-utils/tracking";
import { isEndMatch } from "~/utils/route";

export const Route = createFileRoute(
  "/(restricted)/byggeradar/_access/agent/$agentId",
)({
  component: AgentComponent,
  validateSearch: proposalSearchSchema,
  beforeLoad: async ({
    location,
    search,
    params: { agentId },
    context: { queryClient },
    matches,
    routeId,
  }) => {
    const agents = await ensureAgents(queryClient);
    const preloadedAgent = agents.find((agent) => agent.id === agentId) ?? null;

    // If the search is not marked `suppressLifeCycleRedirects`, we load from the agent's filters
    if (
      !location.state.suppressLifeCycleRedirects &&
      preloadedAgent &&
      isDifferent(search, preloadedAgent.filter)
    ) {
      throw redirect({
        to: location.pathname,
        search: preloadedAgent.filter,
        replace: true,
      });
    }

    // Not on /byggeradar/agent/$agentId/$proposalId: that route sends the proposal to /byggeradar/search/$proposalId.
    if (
      isEndMatch(matches, routeId) &&
      !preloadedAgent &&
      Object.keys(search).length
    ) {
      // if we have any search params, send the user to main search
      throw redirect({ to: "/byggeradar/search", search, replace: true });
    }

    return { preloadedAgent };
  },
  loader: async ({ context: { preloadedAgent, i18n } }) => {
    if (!preloadedAgent) {
      throw notFound();
    }

    await i18n.loadNamespaces("proposals");

    const title = i18n.t("proposals:agent.meta.title", {
      agentName: preloadedAgent.name,
    });

    return { meta: { title } };
  },
  head: async (context) => {
    return {
      meta: [
        {
          title: context.loaderData?.meta.title,
        },
      ],
    };
  },
  notFoundComponent: AgentNotFoundComponent,
});

function AgentComponent() {
  const search = Route.useSearch();
  const { agentId } = Route.useParams();

  const { t } = useTranslation(["proposals"]);

  // to not keep showing that the agent has changes, after update,
  // we need to load this reactively.
  const { agents } = useAgents();

  const { trackEvent } = useByggeRadarTracking();
  const openedAgent = agents.find((agent) => agent.id === agentId);
  useTrackOncePer(openedAgent?.id, () => {
    if (openedAgent) {
      trackEvent("agent_opened", agentTrackingProperties(openedAgent));
    }
  });

  const { updateSearch } = useUpdateSearch(search, Route.fullPath);

  const { page } = useSelectionData(search, () => {
    /* noop, not doing any updates here */
  });

  const { boundingBox, onViewportChange, onReset, searchView } = useMapViewport(
    search,
    updateSearch,
  );

  const source = useMemo<ProposalSource>(
    () => ({ type: "filter", search }),
    [search],
  );

  const {
    isLoading: isProposalsLoading,
    proposals,
    totalSize,
    error,
  } = useProposalsLoader(source, {
    size: PAGINATION_PAGE_SIZE,
    page: page,
    boundingBox,
    agentId,
  });

  const {
    markers,
    isFetching: isMarkersFetching,
    error: markersError,
  } = useProposalsMapMarkers(source);

  return (
    <BaseMapLayout
      topBarContent={
        <AgentTopBar
          search={search}
          agentId={agentId}
          agents={agents}
        />
      }
      listTopContent={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-xl font-bold">
            <IconNotificationsOutline className="size-6" />
            <div>
              {t("proposals:agent.matches_title", {
                amount: totalSize,
              })}
            </div>
          </div>
          <MarkAllSeenButton agentId={agentId} />
        </div>
      }
      listContent={
        <>
          <ProposalList
            isLoading={isProposalsLoading}
            proposals={proposals}
            error={error}
          />
          {totalSize && totalSize > PAGINATION_PAGE_SIZE ? (
            <div className="mt-5 flex w-full justify-center">
              <ProposalPagination
                page={search.page ?? 0}
                pageSize={PAGINATION_PAGE_SIZE}
                totalItems={totalSize}
                scrollSelector="#proposal-list"
                updateSearch={updateSearch}
              />
            </div>
          ) : undefined}
        </>
      }
      mapContent={
        <SearchResultMap
          results={markers}
          source={source}
          isFetching={isMarkersFetching}
          error={markersError}
          routePath={Route.fullPath}
          searchView={searchView}
          onViewportChange={onViewportChange}
          onReset={onReset}
        />
      }
    />
  );
}
